import React, { useState, useEffect } from 'react';
import { IoClose, IoChevronBack, IoChevronForward, IoTrash } from 'react-icons/io5';

interface Prompt {
  id: number;
  prompt_text: string;
  word_count: number;
  primary_category_id: number;
  secondary_category_id: number;
  ai_prompt_specific_advice: string;
}

type SupplementStatus = 'OPTIONAL' | 'REQUIRED' | 'HIGHLY_RECOMMENDED';

interface Supplement {
  id: number;
  name: string;
  supplement_number?: number;
  description?: string;
  min_number_required: number;
  max_number_required: number;
  status: SupplementStatus;
  prompts?: Prompt[];
}

interface PromptCategory {
  id: number;
  name: string;
  overview?: string;
}

interface SupplementDetailsModalProps {
  supplement: Supplement | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (supplement: Supplement) => Promise<void>;
  promptCategories: PromptCategory[];
  supplements?: Supplement[];
}

const SupplementDetailsModal: React.FC<SupplementDetailsModalProps> = ({
  supplement,
  isOpen,
  onClose,
  onSave,
  promptCategories,
  supplements = [],
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formData, setFormData] = useState<Supplement | null>(null);
  const [availableOptions, setAvailableOptions] = useState(2);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (supplement) {
      const isNewSupplement = supplement.id === 0;
      let supplementData = { ...supplement };
      
      if (isNewSupplement) {
        const nextSupplementNumber = supplements.length + 1;
        supplementData = {
          ...supplementData,
          supplement_number: nextSupplementNumber,
          name: supplementData.name || `Supplement ${nextSupplementNumber}`
        };
      }
      
      setFormData(supplementData);
      setAvailableOptions(supplement.prompts?.length || 0);
      setCurrentSlide(0);
    }
  }, [supplement, supplements]);

  if (!isOpen || !formData) return null;

  const totalSlides = availableOptions;

  const updateCarousel = (slide: number) => {
    setCurrentSlide(slide);
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      updateCarousel(currentSlide - 1);
    }
  };

  const handleNext = () => {
    if (currentSlide < totalSlides - 1) {
      updateCarousel(currentSlide + 1);
    }
  };

  const handleAvailableOptionsChange = (value: number) => {
    setAvailableOptions(value);
    if (currentSlide >= value && value > 0) {
      setCurrentSlide(value - 1);
    } else if (value === 0) {
      setCurrentSlide(0);
    }

    if (formData) {
      const currentPrompts = formData.prompts || [];
      const newPrompts = [];
      
      for (let i = 0; i < value; i++) {
        if (i < currentPrompts.length) {
          newPrompts.push(currentPrompts[i]);
        } else {
          newPrompts.push({
            id: Date.now() + i,
            prompt_text: '',
            word_count: 500,
            primary_category_id: categories[0]?.id || 1,
            secondary_category_id: secondaryCategories[0]?.id || 1,
            ai_prompt_specific_advice: ''
          });
        }
      }
      
      setFormData({ ...formData, prompts: newPrompts });
    }
  };

  const updatePrompt = (index: number, field: keyof Prompt, value: any) => {
    if (!formData) return;
    
    const updatedPrompts = [...(formData.prompts || [])];
    
    while (updatedPrompts.length <= index) {
      updatedPrompts.push({
        id: Date.now() + updatedPrompts.length,
        prompt_text: '',
        word_count: 500,
        primary_category_id: categories[0]?.id || 1,
        secondary_category_id: secondaryCategories[0]?.id || 1,
        ai_prompt_specific_advice: ''
      });
    }
    
    updatedPrompts[index] = { ...updatedPrompts[index], [field]: value };
    setFormData({ ...formData, prompts: updatedPrompts });
  };

  const deletePrompt = (indexToDelete: number) => {
    if (!formData || !formData.prompts) return;
    
    const updatedPrompts = formData.prompts.filter((_, index) => index !== indexToDelete);
    const newAvailableOptions = updatedPrompts.length;
    
    setFormData({ ...formData, prompts: updatedPrompts });
    setAvailableOptions(newAvailableOptions);
    
    if (currentSlide >= newAvailableOptions && newAvailableOptions > 0) {
      setCurrentSlide(newAvailableOptions - 1);
    } else if (newAvailableOptions === 0) {
      setCurrentSlide(0);
    }
  };

  const handleSave = async () => {
    if (formData) {
      try {
        setIsSaving(true);
        await onSave(formData);
        onClose();
      } catch (error: any) {
        alert('Error saving: ' + (error?.message || 'Unknown error'));
      } finally {
        setIsSaving(false);
      }
    } else {
      alert('No form data to save');
    }
  };

  const categories = promptCategories;
  const secondaryCategories = promptCategories;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[80vh] flex flex-col">
        
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6 text-white relative flex-shrink-0 rounded-t-2xl">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="absolute top-6 right-8 text-white hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {IoClose({ className: "text-xl" })}
          </button>
          <h2 className="text-2xl font-bold">Supplemental Essay Details</h2>
          <p className="text-blue-100 mt-1">Configure essay requirements and prompts</p>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-8">
          
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-6">Essay Configuration</h3>            
            <div className="grid grid-cols-2 gap-6 mb-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Supplement Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={isSaving}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="e.g., Supplement 2"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Supplement Number</label>
                <input
                  type="number"
                  min="1"
                  value={formData.supplement_number || ''}
                  onChange={(e) => {
                    const newNumber = parseInt(e.target.value) || 1;
                    const newName = `Supplement ${newNumber}`;
                    setFormData({ 
                      ...formData, 
                      supplement_number: newNumber,
                      name: newName
                    });
                  }}
                  disabled={isSaving}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Auto-incremental"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Available Options</label>
                <select
                  value={availableOptions}
                  onChange={(e) => handleAvailableOptionsChange(parseInt(e.target.value))}
                  disabled={isSaving}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {Array.from({ length: 21 }, (_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Minimum # Required</label>
                <select
                  value={formData.min_number_required}
                  onChange={(e) => setFormData({ ...formData, min_number_required: parseInt(e.target.value) })}
                  disabled={isSaving}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {Array.from({ length: 21 }, (_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Maximum # Accepted</label>
                <select
                  value={formData.max_number_required}
                  onChange={(e) => setFormData({ ...formData, max_number_required: parseInt(e.target.value) })}
                  disabled={isSaving}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {Array.from({ length: 21 }, (_, i) => (
                    <option key={i} value={i}>{i}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {totalSlides > 0 && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-800">Essay Details</h3>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handlePrevious}
                    disabled={currentSlide === 0 || isSaving}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {IoChevronBack({ className: "text-gray-600" })}
                  </button>
                  <span className="text-sm text-gray-500 font-medium">
                    {currentSlide + 1} of {totalSlides}
                  </span>
                  <button
                    onClick={handleNext}
                    disabled={currentSlide === totalSlides - 1 || isSaving}
                    className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {IoChevronForward({ className: "text-gray-600" })}
                  </button>
                </div>
              </div>

              <div className="relative overflow-hidden w-full">
                <div 
                  className="flex transition-transform duration-300 ease-in-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {Array.from({ length: totalSlides }, (_, index) => {
                    const prompt = formData.prompts?.[index] || {
                      id: Date.now() + index,
                      prompt_text: '',
                      word_count: 500,
                      primary_category_id: categories[0]?.id || 1,
                      secondary_category_id: secondaryCategories[0]?.id || 1,
                      ai_prompt_specific_advice: ''
                    };

                    return (
                      <div key={index} className="w-full flex-shrink-0 bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex justify-between items-start mb-4">
                          <h4 className="text-lg font-medium text-gray-800">Prompt {index + 1}</h4>
                          <button
                            onClick={() => deletePrompt(index)}
                            disabled={isSaving}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete prompt"
                          >
                            {IoTrash({ className: "text-lg" })}
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-6">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">Prompt</label>
                              <textarea
                                value={prompt.prompt_text}
                                onChange={(e) => updatePrompt(index, 'prompt_text', e.target.value)}
                                disabled={isSaving}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none h-32 disabled:opacity-50 disabled:cursor-not-allowed"
                                placeholder="Enter essay prompt..."
                              />
                            </div>

                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">Word Count</label>
                              <input
                                type="number"
                                value={prompt.word_count}
                                onChange={(e) => updatePrompt(index, 'word_count', parseInt(e.target.value) || 0)}
                                disabled={isSaving}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                                placeholder="500"
                              />
                            </div>
                          </div>

                          <div className="space-y-6">
                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">Category</label>
                              <select
                                value={prompt.primary_category_id}
                                onChange={(e) => updatePrompt(index, 'primary_category_id', parseInt(e.target.value))}
                                disabled={isSaving}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {categories.map((category) => (
                                  <option key={category.id} value={category.id}>{category.name}</option>
                                ))}
                              </select>
                            </div>

                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">Secondary Category</label>
                              <select
                                value={prompt.secondary_category_id}
                                onChange={(e) => updatePrompt(index, 'secondary_category_id', parseInt(e.target.value))}
                                disabled={isSaving}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {secondaryCategories.map((category) => (
                                  <option key={category.id} value={category.id}>{category.name}</option>
                                ))}
                              </select>
                            </div>

                            <div className="space-y-2">
                              <label className="block text-sm font-medium text-gray-700">Model Context</label>
                              <textarea
                                value={prompt.ai_prompt_specific_advice}
                                onChange={(e) => updatePrompt(index, 'ai_prompt_specific_advice', e.target.value)}
                                disabled={isSaving}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none h-20 disabled:opacity-50 disabled:cursor-not-allowed"
                                placeholder="Additional context for AI model..."
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          </div>
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 px-8 py-4 bg-white rounded-b-2xl">
          <div className="flex justify-end space-x-4">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSaving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupplementDetailsModal;